import { useEffect, useState, useDeferredValue, useRef } from "react";
import {
  Box,
  CircularProgress,
  Autocomplete,
  TextField,
  useMediaQuery,
  useTheme,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { getOpenSearchData, resetOpenSearch } from "../../service/nurtureSlice";
import { useAuthUser } from "react-auth-kit";
import ClearIcon from "@mui/icons-material/Clear";
import { getSubDomainAccessStatus } from "../../../../../components/RolePermissionAccess/RolePermissionAccess";
import { openLeadExpandModal } from "../../../../../components/LeadPopup/hooks/useLeadCard";

const OpenSearch = ({
  fromNurture,
  selectedCourse,
  placeholder = "Search Leads",
}: any) => {
  const auth = useAuthUser();
  const authUser = auth();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isTabSize = useMediaQuery(theme.breakpoints.down("lg"));
  const { userById } = useSelector((state: any) => state.userDetails);

  const hasAllBranchAccess = getSubDomainAccessStatus(
    "GLOBAL_PERMISSIONS",
    "BRANCH_ACCESS",
    "ALL_BRANCHES",
    userById?.rolePermission
  );
  const hasSelfAssignedLeadOnly = getSubDomainAccessStatus(
    "NURTURE_BOARD",
    "LEAD_ACCESS",
    "ASSIGNED_ONLY",
    userById?.rolePermission
  );
  const [leadSearchTerm, setLeadSearchTerm] = useState<string>("");
  const [options, setOptions] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const deferredQuery = useDeferredValue(leadSearchTerm);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const openSearchData = useSelector(
    (state: any) => state.nurture.openSearchData
  );
  const openSearchLoading = useSelector(
    (state: any) => state.nurture.openSearchLoading
  );
  const courseList = useSelector((state: any) => state.courses.courses);

  const handleSearch = (event: any, newValue: string) => {
    setLeadSearchTerm(newValue);
  };
  useEffect(() => {
    const fetchSearchResults = () => {
      setLoading(true);
      setOptions([]);
      const queryParams = new URLSearchParams(window.location.search);
      const courseId = queryParams.get("selectedCourse");
      const leadStatus = queryParams.get("nurtureLeadStatus");
      const body = {
        entityName: "ENQUIRY",
        searchText: deferredQuery,
        instituteId: authUser?.institute[0]?.instituteId,
        courseId: courseId,
        branchId: hasAllBranchAccess ? "" : userById?.branchId,
        enquiryStatus: leadStatus === "LOST" ? "LOST" : "ACTIVE",
        assignedToId: hasSelfAssignedLeadOnly ? userById?.id : "",
      };
      const studentBody = {
        entityName: "BATCHSTUDENTMAPPING",
        searchText: deferredQuery,
        instituteId: authUser?.institute[0]?.instituteId,
        courseId: "",
        branchId: hasAllBranchAccess ? "" : userById?.branchId,
        assignedToId: hasSelfAssignedLeadOnly ? userById?.id : "",
      };
      dispatch(resetOpenSearch());
      if (deferredQuery !== "") {
        dispatch<any>(getOpenSearchData(fromNurture ? body : studentBody));
      }
      setLoading(false);
    };
    fetchSearchResults();
  }, [deferredQuery]);

  useEffect(() => {
    if (openSearchData?.hits?.hits) {
      setOptions(openSearchData?.hits?.hits);
    }
  }, [openSearchData, setOptions]);

  useEffect(() => {
    return () => {
      setLeadSearchTerm("");
    };
  }, [selectedCourse]);
  const handleClearSearch = () => {
    setLeadSearchTerm("");
  };
  const open = Boolean(anchorEl);
  const handleClose = () => {
    setAnchorEl(null);
  };

  const showCourseName = (id: string) => {
    const courseName =
      courseList && courseList?.filter((course: any) => course?.id === id);
    return courseName[0]?.courseName;
  };

  const queryParams = new URLSearchParams(window.location.search);
  const isModalOPen = queryParams.get("leadModal");

  const isActiveLostTriggered = useSelector(
    (state: any) => state.openSearchUpdation?.isActiveLostTriggered
  );

  useEffect(() => {
    setLeadSearchTerm("");
  }, [isActiveLostTriggered]);

  return (
    <div
      style={{ display: "flex", flexDirection: "column", position: "relative" }}
    >
      {" "}
      <Autocomplete
        freeSolo
        disableClearable={false}
        options={leadSearchTerm ? options.map((option) => option?._source) : []}
        getOptionLabel={(option: any) =>
          [
            option?.studentFirstName || "",
            option?.studentLastName || "",
            option?.studentContact || "",
            option?.studentEmail || "",
            option?.searchStudentId || "",
          ]
            ?.filter(Boolean)
            ?.join(" ")
        }
        size="small"
        inputValue={leadSearchTerm}
        onInputChange={handleSearch}
        loading={loading}
        onChange={(event, newValue) => {
          if (newValue) {
            openLeadExpandModal(
              newValue.instituteId,
              newValue.studentContact,
              newValue.studentEmail,
              fromNurture ? newValue?.id : newValue?.enquiryId,
              dispatch,
              "overview"
            );
          }
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={placeholder}
            variant="outlined"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {" "}
                  {leadSearchTerm && (
                    <InputAdornment position="end" sx={{ display: "flex" }}>
                      {" "}
                      <IconButton onClick={handleClearSearch} edge="end">
                        {" "}
                        <ClearIcon />{" "}
                      </IconButton>{" "}
                    </InputAdornment>
                  )}{" "}
                </>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": { paddingRight: "10px !important" },
              "& .MuiAutocomplete-inputRoot": {
                paddingRight: "10px !important",
              },
            }}
          />
        )}
        sx={{
          width: isTabSize ? "150px" : "300px",
        }}
        renderOption={(props, option: any) => {
          return (
            <div
              key={option?.id}
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: "10px",
                padding: "0px 10px",
                cursor: "pointer",
                marginBottom: "5px",
              }}
              onClick={(event: any) => {
                openLeadExpandModal(
                  option?.instituteId,
                  option?.studentContact,
                  option?.studentEmail,
                  fromNurture ? option?.id : option?.enquiryId,
                  dispatch,
                  "overview"
                );
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  cursor: "pointer",
                  flexDirection: "column",
                }}
              >
                <div style={{ fontSize: "14px", fontWeight: "500" }}>
                  {`${option?.studentFirstName || ""} ${
                    option?.studentLastName || ""
                  }`}
                </div>
                <div
                  style={{
                    fontSize: "10px",
                    fontWeight: "400",
                    overflow: "hidden",
                  }}
                >
                  {option?.searchStudentId}
                </div>
                <div
                  style={{
                    fontSize: "10px",
                    fontWeight: "400",
                    overflow: "hidden",
                  }}
                >
                  {`${option?.studentEmail ? option?.studentEmail : ""} ${
                    option?.studentContact
                      ? `+${option?.contactCountryCode || "91"} ${
                          option?.contactCountryCode
                            ? option?.studentContact?.replace(
                                option?.contactCountryCode,
                                ""
                              )
                            : option?.studentContact?.startsWith("91")
                            ? option?.studentContact?.replace("91", "")
                            : option?.studentContact
                        }`
                      : ""
                  }`}
                </div>
                <div
                  style={{
                    fontSize: "10px",
                    fontWeight: "400",
                    overflow: "hidden",
                  }}
                >
                  {showCourseName(option?.courseId)}
                </div>
              </div>
            </div>
          );
        }}
      />
      {leadSearchTerm && (openSearchLoading || options.length === 0) && (
        <Box
          padding={"1.25px 18px"}
          marginTop={"7px"}
          display={"flex"}
          justifyContent={"space-between"}
          alignItems={"center"}
          borderRadius={"8px"}
          border={"1px solid #E4E4E7"}
          width={"calc(100% - 0px)"}
          position={"absolute"}
          top="35px"
          zIndex={isModalOPen === "true" ? "0" : "99999999"}
          bgcolor={"#fff"}
          height={"150px"}
        >
          {" "}
          {openSearchLoading && (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
                width: "100%",
              }}
            >
              {" "}
              <CircularProgress size={24} />{" "}
            </div>
          )}{" "}
          {leadSearchTerm && !openSearchLoading && options.length === 0 && (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
                width: "100%",
              }}
            >
              {" "}
              No result found{" "}
            </div>
          )}{" "}
        </Box>
      )}{" "}
    </div>
  );
};
export default OpenSearch;
